import { LogisticIntegrationsService } from "../../../services/logistic/logisticIntegrations.ts"; // Service for logistic integrations
import { ArticlesService } from "../../../services/logistic/articles.ts"; // Service for articles
import { CarriersService } from "../../../services/registry/carriers.ts";
import { CompaniesService } from "../../../services/registry/companies.ts";
import { FetchParam } from "../../../Models/FetchParam"; // Defines fetch parameters structure
import { prepareFetchParam } from "../../../api/apiHelpers.ts"; // Utility function to prepare fetch parameters
import { callErrorToast } from "../../../utilities"; // Utility to show error toasts
import { CodCollectionsService } from "../../../services/shipment/codCollections.ts";
import { CodLiquidation } from "../../../Models/CodLiquidation.ts";
import { StoreIntegrationsService } from "../../../services/logistic/storeIntegrations.ts";
import { AddressBookService as RegistryAddressBookService } from "../../../services/registry/addressBook.ts";
import { CarrierConfigurationsService } from "../../../services/contract/carrierConfigurations.ts";
import { CollectionsService } from "../../../services/shipment/collections.ts";

const logisticIntegrationsService = new LogisticIntegrationsService();
const storeIntegrationsService = new StoreIntegrationsService();
const articlesService = new ArticlesService();
const carriersService = new CarriersService();
const companiesService = new CompaniesService();
const codCollectionsService = new CodCollectionsService();
const registryAddressBookService = new RegistryAddressBookService();
const carrierConfigurationsService = new CarrierConfigurationsService();
const collectionsService = new CollectionsService();

/**
 * Fetches all companies based on the provided search parameters.
 * It prepares the necessary filters before making the API request.
 *
 * @param {FetchParam} searchParams - The search parameters including input value, pagination, sorting, and optional search filters.
 * @returns {Promise<any>} - A promise resolving with the response data.
 * @throws {Error} - Throws an error if the request fails.
 */
export const getAllCompanies = async (searchParams: FetchParam) => {
  try {
    const filters = prepareFetchParam(searchParams);

    // Make the API call to get all companies with the prepared filters
    const response = await companiesService.all(filters);

    return response;
  } catch (err) {
    callErrorToast(err);
    throw err;
  }
};

/**
 * Fetches all companies based on the provided search parameters.
 * It prepares the necessary filters before making the API request.
 *
 * @returns {Promise<any>} - A promise resolving with the response data.
 * @throws {Error} - Throws an error if the request fails.
 */
export const getAllLiquidationCompanies = async (
  codLiquidation: Partial<CodLiquidation>,
) => {
  try {
    const response =
      await codCollectionsService.liquidationCompanies(codLiquidation);
    return response;
  } catch (err) {
    callErrorToast(err);
    throw err;
  }
};

/**
 * Fetches all companies based on the provided search parameters.
 * It prepares the necessary filters before making the API request.
 *
 * @returns {Promise<any>} - A promise resolving with the response data.
 * @throws {Error} - Throws an error if the request fails.
 */
export const getAllArticlesCompanies = async () => {
  try {
    const response = await articlesService.articlesCompanies();
    return response;
  } catch (err) {
    callErrorToast(err);
    throw err;
  }
};

/**
 * Fetches all logistic integrations based on the provided search parameters.
 * It prepares the necessary filters before making the API request.
 *
 * @param {FetchParam} searchParams - The search parameters including input value, pagination, sorting, and optional search filters.
 * @returns {Promise<any>} - A promise resolving with the response data.
 * @throws {Error} - Throws an error if the request fails.
 */
export const getAllLogisticIntegrations = async (searchParams: FetchParam) => {
  try {
    const filters = prepareFetchParam(searchParams);
    const response = await logisticIntegrationsService.all(filters);
    return response;
  } catch (err) {
    callErrorToast(err);
    throw err;
  }
};

/**
 * Fetches all logistic integrations based on the provided search parameters.
 * It prepares the necessary filters before making the API request.
 *
 * @param {FetchParam} searchParams - The search parameters including input value, pagination, sorting, and optional search filters.
 * @returns {Promise<any>} - A promise resolving with the response data.
 * @throws {Error} - Throws an error if the request fails.
 */
export const getAllOrderGenericFields = async (id: string) => {
  try {
    const response = await companiesService.getGenericFieldsByType(id, "ORDER");
    return response;
  } catch (err) {
    callErrorToast(err);
    throw err;
  }
};

/**
 * Fetches all logistic integrations based on the provided search parameters.
 * It prepares the necessary filters before making the API request.
 *
 * @param {FetchParam} searchParams - The search parameters including input value, pagination, sorting, and optional search filters.
 * @returns {Promise<any>} - A promise resolving with the response data.
 * @throws {Error} - Throws an error if the request fails.
 */
export const getAllStoreIntegrations = async (searchParams: FetchParam) => {
  try {
    const filters = prepareFetchParam(searchParams);
    const response = await storeIntegrationsService.all(filters);
    return response;
  } catch (err) {
    callErrorToast(err);
    throw err;
  }
};

/**
 * Fetches all articles based on the provided search parameters.
 * It prepares the necessary filters before making the API request.
 *
 * @param {FetchParam} searchParams - The search parameters including input value, pagination, sorting, and optional search filters.
 * @returns {Promise<any>} - A promise resolving with the response data.
 * @throws {Error} - Throws an error if the request fails.
 */
export const getAllArticles = async (searchParams: FetchParam) => {
  try {
    const filters = prepareFetchParam(searchParams);
    const response = await articlesService.all(filters);
    return response;
  } catch (err) {
    callErrorToast(err);
    throw err;
  }
};

/**
 * Fetches all articles based on the provided search parameters.
 * It prepares the necessary filters before making the API request.
 *
 * @param {string} carrierCode -
 * @returns {Promise<any>} - A promise resolving with the response data.
 * @throws {Error} - Throws an error if the request fails.
 */
export const getStockReleasesByCarrier = async (carrierCode: string) => {
  try {
    const response = await carriersService.getStockReleases(carrierCode);
    return response;
  } catch (err) {
    callErrorToast(err);
    throw err;
  }
};

/**
 * Fetches all carriers based on the provided search parameters.
 * It prepares the necessary filters before making the API request.
 *
 * @param {FetchParam} searchParams -
 * @returns {Promise<any>} - A promise resolving with the response data.
 * @throws {Error} - Throws an error if the request fails.
 */
export const getAllCarriers = async (searchParams: FetchParam) => {
  try {
    const filters = prepareFetchParam(searchParams);

    // Make the API call to get all carriers with the prepared filters
    const response = await carriersService.all(filters);
    return response;
  } catch (err) {
    callErrorToast(err);
    throw err;
  }
};

export const getAllCarrierConfigurations = async (searchParams: FetchParam) => {
  try {
    const filters = prepareFetchParam(searchParams);

    const response = await carrierConfigurationsService.all(filters);
    return response;
  } catch (err) {
    callErrorToast(err);
    throw err;
  }
};

export const getAllCarriersFromCarrierConfigurations = async (
  searchParams: FetchParam,
) => {
  try {
    const filters = prepareFetchParam(searchParams);

    const response = await carrierConfigurationsService.all(filters);
    response.content = response.content?.filter(
      (item: any, index: number, self: any[]) =>
        index === self.findIndex((t) => t.carrierCode === item.carrierCode),
    );
    return response;
  } catch (err) {
    callErrorToast(err);
    throw err;
  }
};

/* Fetches all articles based on the provided search parameters.
 * It prepares the necessary filters before making the API request.
 *
 * @param {FetchParam} searchParams - The search parameters including input value, pagination, sorting, and optional search filters.
 * @returns {Promise<any>} - A promise resolving with the response data.
 * @throws {Error} - Throws an error if the request fails.
 */
export const getAllRegistryAddressBook = async (searchParams: FetchParam) => {
  try {
    const filters = prepareFetchParam(searchParams);
    const response = await registryAddressBookService.all(filters);
    return response;
  } catch (err) {
    callErrorToast(err);
    throw err;
  }
};

/**
 * Retrieves all COD (Cash On Delivery) suppliers from the backend service.
 *
 * This function calls the `codCollectionsService.getSuppliers()` method to fetch
 * the list of suppliers handling COD operations.
 * In case of an error, it triggers a toast notification and rethrows the error.
 *
 * @async
 * @function getAllCODSuppliers
 * @returns {Promise<any>} A promise that resolves with the list of COD suppliers.
 * @throws {Error} If the API request fails.
 */
export const getAllCODSuppliers = async () => {
  try {
    const response = await codCollectionsService.getSuppliers();
    return response;
  } catch (err) {
    callErrorToast(err);
    throw err;
  }
};

export const getCodCollectionCustomers = async () => {
  try {
    const response = await codCollectionsService.getCustomers();
    return response;
  } catch (err) {
    callErrorToast(err);
    throw err;
  }
};

export const getCollectionOwners = async () => {
  try {
    const response = await collectionsService.getOwners();
    return response;
  } catch (err) {
    callErrorToast(err);
    throw err;
  }
};
